import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  IconButton
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import {
  TrendingUp,
  Warning,
  CheckCircle,
  Insights,
  Close,
  Inventory,
  Schedule,
  LocationOn,
  Assessment
} from '@mui/icons-material';

const BusinessInsightsDialog = ({ open, onClose, skuData, warehouseId }) => {
  // Basic null check to prevent errors
  if (!skuData) {
    return null;
  }

  const getInsightDetails = () => {
    const details = [];
    
    // Hardcoded High Risk Alert - only for SKU 1692502122
    if (skuData.sku === '1692502122') {
      details.push({
        type: 'high_risk_alert',
        title: 'High Risk Alert',
        icon: <Warning sx={{ color: 'error.main' }} />,
        description: 'This item has exceeded its typical order pattern. Last order is 22 days ago and beyond typical 21-day cycle.',
        recommendation: 'Immediate action required. Contact customer to confirm order status and consider proactive outreach.',
        severity: 'alert',
        hasChart: true
      });
    }
    
    // High quantity insight
    details.push({
      type: 'high_quantity',
      title: 'High Inventory Level',
      icon: <TrendingUp sx={{ color: 'success.main' }} />,
      description: `Current quantity: ${skuData.total_quantity?.toLocaleString() || 0} units`,
      recommendation: 'Consider reducing stock levels to optimize storage space and reduce carrying costs.',
      severity: 'info'
    });
    
    // Expiring soon insight
    details.push({
      type: 'expiring_soon',
      title: 'Lots Expiring Soon',
      icon: <Warning sx={{ color: 'warning.main' }} />,
      description: `${skuData.expiring_soon_count || 0} lot(s) will expire within 30 days`,
      recommendation: 'Prioritize these lots for immediate use or consider discounting to move inventory quickly.',
      severity: 'warning'
    });
    
    // Expired lots insight
    details.push({
      type: 'expired',
      title: 'Expired Lots',
      icon: <Warning sx={{ color: 'error.main' }} />,
      description: `${skuData.expired_count || 0} lot(s) have expired`,
      recommendation: 'Immediate action required. Remove expired inventory and investigate root cause.',
      severity: 'alert'
    });
    
    // Scattered inventory insight
    details.push({
      type: 'scattered',
      title: 'Scattered Inventory',
      icon: <Insights sx={{ color: 'info.main' }} />,
      description: `Inventory spread across ${skuData.unique_areas || 0} areas and ${skuData.unique_zones || 0} zones`,
      recommendation: 'Consider consolidating inventory to improve picking efficiency and reduce travel time.',
      severity: 'info'
    });
    
    // Optimal condition insight
    details.push({
      type: 'optimal',
      title: 'Optimal Inventory Condition',
      icon: <CheckCircle sx={{ color: 'success.main' }} />,
      description: 'Inventory levels and conditions are within optimal ranges',
      recommendation: 'Maintain current practices. This SKU is well-managed.',
      severity: 'success'
    });
    
    return details;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'alert': return 'error.main';
      case 'warning': return 'warning.main';
      case 'success': return 'success.main';
      case 'info': return 'info.main';
      default: return 'text.secondary';
    }
  };

  // Weekly order pattern data
  const weeklyOrderData = [
    { day: 'Monday', orders: 2, color: '#8884d8' },
    { day: 'Tuesday', orders: 32, color: '#82ca9d' },
    { day: 'Wednesday', orders: 1, color: '#8884d8' },
    { day: 'Thursday', orders: 2, color: '#8884d8' },
    { day: 'Friday', orders: 2, color: '#8884d8' },
    { day: 'Saturday', orders: 0, color: '#8884d8' },
    { day: 'Sunday', orders: 0, color: '#8884d8' }
  ];

  // Order trend line chart data
  const orderTrendData = [
    { week: 21, actual: 105, bestModel: 80, baseline: 60 },
    { week: 22, actual: 0, bestModel: 12, baseline: 60 },
    { week: 23, actual: 105, bestModel: 85, baseline: 60 },
    { week: 24, actual: 0, bestModel: 12, baseline: 60 },
    { week: 25, actual: 0, bestModel: 18, baseline: 60 },
    { week: 26, actual: 105, bestModel: 75, baseline: 60 },
    { week: 27, actual: 0, bestModel: 10, baseline: 60 },
    { week: 28, actual: 105, bestModel: 90, baseline: 60 },
    { week: 29, actual: 0, bestModel: 12, baseline: 60 },
    { week: 30, actual: 105, bestModel: 80, baseline: 60 }
  ];

  // Reorder frequency distribution data
  const reorderDistributionData = [
    { days: 7, frequency: 2 },
    { days: 14, frequency: 8 },
    { days: 21, frequency: 12 },
    { days: 28, frequency: 6 },
    { days: 35, frequency: 3 },
    { days: 42, frequency: 1 },
    { days: 49, frequency: 0 },
    { days: 56, frequency: 1 },
  ];

  const insightDetails = getInsightDetails();

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment />
          <Typography variant="h6">
            Business Insights - {skuData.sku}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* SKU Summary */}
        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  {skuData.product_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  SKU: {skuData.sku}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Warehouse: {warehouseId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Inventory sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2">
                      Total Quantity: <strong>{skuData.total_quantity?.toLocaleString()}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2">
                      Locations: <strong>{skuData.unique_areas} areas, {skuData.unique_zones} zones</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2">
                      Lots: <strong>{skuData.unique_lots}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Insights List */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Key Insights
          </Typography>
          <List sx={{ p: 0 }}>
            {insightDetails.map((insight, index) => (
              <React.Fragment key={insight.type}>
                <ListItem sx={{ 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  mb: 1,
                  border: `1px solid ${getSeverityColor(insight.severity)}20`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, width: '100%' }}>
                    {insight.icon}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      {insight.title}
                    </Typography>
                    <Chip 
                      label={insight.severity.toUpperCase()} 
                      size="small" 
                      color={insight.severity}
                      variant="outlined"
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {insight.description}
                  </Typography>
                  
                  <Alert severity={insight.severity === 'alert' ? 'error' : insight.severity} sx={{ width: '100%' }}>
                    <Typography variant="body2">
                      <strong>Recommendation:</strong> {insight.recommendation}
                    </Typography>
                  </Alert>
                  
                  {/* Charts Section for High Risk Alert */}
                  {insight.hasChart && (
                    <Box sx={{ mt: 2, width: '100%' }}>
                      <Grid container spacing={0} sx={{ width: '100%' }}>
                        {/* Chart 1: Weekly Order Pattern */}
                        <Grid item xs={12} md={4} sx={{ p: 1 }}>
                          <Typography variant="subtitle2" gutterBottom align="center">
                            Typical Request Delivery Date: Tuesday
                          </Typography>
                          <Box sx={{ height: 250, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={weeklyOrderData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="day" />
                                <YAxis />
                                <Tooltip 
                                  formatter={(value, name) => [value, 'Orders']}
                                  labelFormatter={(label) => `${label}`}
                                />
                                <Bar 
                                  dataKey="orders" 
                                  fill="#8884d8"
                                  radius={[4, 4, 0, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Grid>

                        {/* Chart 2: Order Trend Line Chart */}
                        <Grid item xs={12} md={4} sx={{ p: 1 }}>
                          <Typography variant="subtitle2" gutterBottom align="center">
                            Typical Shipment: 105 units
                          </Typography>
                          <Box sx={{ height: 250, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={orderTrendData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="week" />
                                <YAxis />
                                <Tooltip 
                                  formatter={(value, name) => [value, 'Units']}
                                  labelFormatter={(label) => `Week ${label}`}
                                />
                                <Legend />
                                <Line 
                                  dataKey="actual" 
                                  stroke="#ff88ff" 
                                  strokeWidth={0}
                                  strokeDasharray="5 5"
                                  dot={{ fill: "#ff88ff", strokeWidth: 2, r: 4 }}
                                  name="Actual Orders"
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="bestModel" 
                                  stroke="#82ca9d" 
                                  strokeWidth={2}
                                  name="Periodic Model"
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="baseline" 
                                  stroke="#ff7300" 
                                  strokeWidth={2}
                                  strokeDasharray="5 5"
                                  name="Croston's Model"
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                        </Grid>

                        {/* Chart 3: Reorder Frequency Distribution */}
                        <Grid item xs={12} md={4} sx={{ p: 1 }}>
                          <Typography variant="subtitle2" gutterBottom align="center">
                            Reorder Frequency:every 21 (or 14) days
                          </Typography>
                          <Box sx={{ height: 250, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={reorderDistributionData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="days" />
                                <YAxis />
                                <Tooltip 
                                  formatter={(value, name) => [value, 'Frequency']}
                                  labelFormatter={(label) => `${label} days`}
                                />
                                <Bar 
                                  dataKey="frequency" 
                                  fill="#ffc658"
                                  radius={[4, 4, 0, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </ListItem>
                {index < insightDetails.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* Additional Metrics */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Additional Metrics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary.main">
                    {skuData.unique_lots}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Lots
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="info.main">
                    {skuData.unique_po_numbers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Purchase Orders
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="warning.main">
                    {skuData.expiring_soon_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expiring Soon
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="error.main">
                    {skuData.expired_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expired Lots
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button 
          variant="contained" 
          onClick={() => {
            // TODO: Implement action to navigate to detailed inventory view
            console.log('Navigate to detailed view for SKU:', skuData.sku);
            onClose();
          }}
        >
          View Detailed Inventory
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessInsightsDialog; 